import { useApp } from "./AppContext";
import type { UserSettings } from "./api";

export type UiMode = UserSettings["uiMode"];

/**
 * UI-16 — which of the two surfaces the desktop shell is wearing.
 *
 * Stored on `user_settings.ui_mode`, which has been in the schema since migration 0001 and
 * in TABLES.md since it was written: this milestone is where the column finally gets a
 * control and a reader, not where it gets a column. Stored rather than kept per device
 * because it is a statement about the person, not about the screen — somebody who should
 * not have to know what a diarizer is should not meet one by signing in on a laptop.
 *
 * Read through the app context's live `settings` rather than from `me.settings`, which is
 * loaded once at start-up: switching mode has to rewrite the rail immediately, and a copy
 * taken at sign-in would mean the switch did nothing until a reload.
 *
 * Full unless the Server says otherwise, including while the settings read is still in
 * flight. The alternative — assuming simple until told — would flash the reduced rail on
 * every cold load for the owner, who lives in full mode.
 */
export function useUiMode(): UiMode {
  const { settings } = useApp();
  return settings?.uiMode ?? "full";
}

/**
 * ARCH-8/UI-24 — whether the developer-only surfaces are showing: the recording Debug tab,
 * the processing panel's step expansion, and the "Insight processing" rail destination.
 *
 * Replaces the `settings?.developerMode ?? false` read each of those sites wrote out by
 * hand — the same reason `useUiMode` exists for `uiMode` rather than every reader repeating
 * the fallback.
 */
export function useDeveloperMode(): boolean {
  const { settings } = useApp();
  return settings?.developerMode ?? false;
}

/**
 * Where each mode opens, and where a mode switch sends somebody.
 *
 * The two are different on purpose. Full mode's home is Recordings, at the head of the
 * pipeline; simple mode's is the world view, which is what the surface is for — the same
 * split the phone app already makes, and for the same reason.
 */
export const FULL_HOME = "/recordings";
export const SIMPLE_HOME = "/world-view";

export function homeFor(mode: UiMode): string {
  return mode === "simple" ? SIMPLE_HOME : FULL_HOME;
}
